import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { AlertService, CurrencyInputComponent, ModalComponent } from 'common';
import { TranslateModule } from '@ngx-translate/core';
import { provideIcons } from '@ng-icons/core';
import { heroInformationCircleMini } from '@ng-icons/heroicons/mini';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EthUnitUtil } from '@31third/common';
import BigDecimal from 'js-big-decimal';
import {
  RebalancingRepository,
  RebalancingState,
} from '../../../../repository';

@Component({
  selector: 'common-rebalancing-sell-portfolio-value-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    CurrencyInputComponent,
  ],
  providers: [
    provideIcons({
      heroInformationCircleMini,
    }),
    CurrencyPipe,
  ],
  templateUrl: './sell-portfolio-value-modal.component.html',
  styleUrls: ['./sell-portfolio-value-modal.component.scss'],
})
export class SellPortfolioValueModalComponent implements OnChanges {
  @Input()
  public isOpen = false;

  @Output()
  public isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public value: number | undefined;
  public maxValue = 0;
  public percentage = 0;

  constructor(
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly alertService: AlertService,
    private readonly currencyPipe: CurrencyPipe,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.maxValue = this.rebalancingRepository.getPortfolioValue();
  }

  public get store(): RebalancingState {
    return this.rebalancingRepository.store.getValue();
  }

  public handleIsOpenChange(isOpen: boolean): void {
    this.resetModal();
    this.isOpenChange.emit(isOpen);
  }

  private resetModal(): void {
    this.value = undefined;
  }

  public onCloseClick(): void {
    this.handleIsOpenChange(false);
  }

  public onValueChange(value: number): void {
    this.value = value;
    this.percentage = (value / this.maxValue) * 100;
  }

  public onSellPortfolioValue(): void {
    if (!this.value || this.value < 0 || this.value > this.maxValue) {
      const formattedMaxValue = this.currencyPipe.transform(
        this.maxValue,
        'USD',
        'symbol',
        '1.2-2',
        this.locale,
      );
      this.alertService.showError('sellPortfolioValueModal.error', {
        maxValue: formattedMaxValue,
      });
      return;
    }

    this.store.sellEntries.forEach(entry => {
      const fullAmount = EthUnitUtil.convertBigIntToBigDecimal(
        entry.asset.balance,
        entry.asset.token.decimals,
      );
      entry.amount = fullAmount
        .multiply(new BigDecimal(this.percentage / 100))
        .getValue();
    });

    const formattedValue = this.currencyPipe.transform(
      this.value,
      'USD',
      'symbol',
      '1.2-2',
      this.locale,
    );
    this.alertService.showSuccess('sellPortfolioValueModal.success', {
      value: formattedValue,
    });
    this.handleIsOpenChange(false);
  }
}
